import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @extends {Component}
 */
export class AnnouncementBar extends Component {
  static get observedAttributes() {
    return ['autoplay'];
  }

  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #focusSuspended = false;

  /**
   * The animation frame used to announce a manually selected slide.
   * @type {number|undefined}
   */
  #announcementFrame = undefined;

  /**
   * Cancels stale section refreshes when cart updates happen quickly.
   * @type {AbortController | undefined}
   */
  #refreshAbortController = undefined;

  /**
   * Keeps autoplay in sync when Shopify re-renders blocks inside the section.
   * @type {MutationObserver}
   */
  #slidesObserver = new MutationObserver(() => {
    this.current = this.current;
    this.#syncPlayback();
  });

  /**
   * Restart autoplay when the theme editor updates the speed setting.
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'autoplay' || oldValue === newValue || !this.isConnected) return;

    this.suspend();
    this.play();
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.addEventListener('theme:countdown:hide', this.#handleCountdownHide);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (slidesContainer) this.#slidesObserver.observe(slidesContainer, { childList: true });

    this.current = 0;
    if (prefersReducedMotion()) this.paused = true;
    this.#syncPlayback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.removeEventListener('theme:countdown:hide', this.#handleCountdownHide);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#slidesObserver.disconnect();
    this.#refreshAbortController?.abort();
    if (this.#announcementFrame !== undefined) {
      cancelAnimationFrame(this.#announcementFrame);
      this.#announcementFrame = undefined;
    }
    this.#focusSuspended = false;
    this.suspend();
  }

  next() {
    this.#advance(1, true);
  }

  previous() {
    this.#advance(-1, true);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !interval || this.#interval || this.#focusSuspended) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.matches(':focus-within') || document.hidden) return;

      this.#advance(1, false);
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.#focusSuspended = false;
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);

    const playbackButton = /** @type {HTMLElement | null} */ (this.querySelector('.announcement-bar__playback'));
    const label = paused ? playbackButton?.dataset.playLabel : playbackButton?.dataset.pauseLabel;
    if (label) playbackButton?.setAttribute('aria-label', label);
  }

  togglePlayback() {
    const shouldPlay = this.paused;
    this.#focusSuspended = false;
    shouldPlay ? this.play() : this.pause();
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (
      !this.autoplay ||
      this.paused ||
      this.#focusSuspended ||
      this.matches(':hover') ||
      this.matches(':focus-within') ||
      document.hidden
    )
      return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval && this.slides.length > 1);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.slides;
    if (!slides.length) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Changes slides and controls whether assistive technologies announce the update.
   * @param {number} offset
   * @param {boolean} announce
   */
  #advance(offset, announce) {
    this.current += offset;
    if (announce) this.#announceCurrentSlide();
  }

  #announceCurrentSlide() {
    const status = this.querySelector('.announcement-bar__status');
    const activeSlide = this.slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
    if (!status || !activeSlide) return;

    const content = activeSlide.querySelector('.announcement-bar__text, .announcement-bar__countdown');
    if (!(content instanceof HTMLElement)) return;

    const announcement = content.innerText.replace(/\s+/g, ' ').trim();
    if (!announcement) return;

    if (this.#announcementFrame !== undefined) cancelAnimationFrame(this.#announcementFrame);
    status.textContent = '';
    this.#announcementFrame = requestAnimationFrame(() => {
      status.textContent = announcement;
      this.#announcementFrame = undefined;
    });
  }

  #getFreeShippingMessages() {
    const messages = new Map();

    for (const slide of this.querySelectorAll('.announcement-bar__slide--free-shipping')) {
      if (!(slide instanceof HTMLElement)) continue;

      const blockId = slide.dataset.announcementBlockId;
      const message = slide.querySelector('.announcement-bar__text');
      if (!blockId || !(message instanceof HTMLElement)) continue;

      messages.set(blockId, message.textContent?.replace(/\s+/g, ' ').trim() ?? '');
    }

    return messages;
  }

  get slides() {
    return Array.from(this.querySelectorAll('slideshow-slide[ref="slides[]"]:not(.hidden)'));
  }

  /**
   * Keeps expired countdown slides out of autoplay without re-rendering the section.
   * @param {Event} event
   */
  #handleCountdownHide = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('slideshow-slide[ref="slides[]"]');
    slide?.classList.add('hidden');

    if (!this.slides.length) {
      this.closest('.shopify-section')?.classList.add('hidden');
      this.suspend();
      return;
    }

    this.current = this.current;
    this.#syncPlayback();
  };

  #syncPlayback() {
    this.#syncControls();

    if (!this.autoplay) {
      this.suspend();
      return;
    }

    if (!this.paused) {
      this.play();
    }
  }

  #syncControls() {
    const hasMultipleSlides = this.slides.length > 1;

    for (const control of this.querySelectorAll('.slideshow-control')) {
      control.classList.toggle('hidden', !hasMultipleSlides);
      if (control instanceof HTMLButtonElement) control.disabled = !hasMultipleSlides;
    }
  }

  /**
   * Refreshes Liquid-rendered cart-dependent announcement content after Ajax cart changes.
   * @param {Event} event
   */
  #handleCartUpdate = async (event) => {
    const cartEvent = /** @type {import('@theme/events').CartUpdateEvent} */ (event);
    if (cartEvent.detail?.data?.didError) return;

    const sectionId = this.sectionId;
    if (!sectionId) return;

    this.#refreshAbortController?.abort();
    this.#refreshAbortController = new AbortController();
    const previousFreeShippingMessages = this.#getFreeShippingMessages();

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), {
        signal: this.#refreshAbortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) return;

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const updatedSlides = documentFragment.querySelector('announcement-bar-component .announcement-bar__slides');
      const currentSlides = this.querySelector('.announcement-bar__slides');

      if (!currentSlides) return;

      if (!updatedSlides) {
        this.closest('.shopify-section')?.classList.add('hidden');
        this.suspend();
        return;
      }

      this.closest('.shopify-section')?.classList.remove('hidden');
      morph(currentSlides, updatedSlides);
      this.current = this.current;
      this.#syncPlayback();

      const activeFreeShippingSlide = this.querySelector(
        '.announcement-bar__slide--free-shipping[aria-hidden="false"]',
      );
      const activeBlockId =
        activeFreeShippingSlide instanceof HTMLElement
          ? activeFreeShippingSlide.dataset.announcementBlockId
          : undefined;
      const updatedFreeShippingMessage = activeBlockId ? this.#getFreeShippingMessages().get(activeBlockId) : undefined;

      if (
        activeBlockId &&
        updatedFreeShippingMessage &&
        updatedFreeShippingMessage !== previousFreeShippingMessages.get(activeBlockId)
      ) {
        this.#announceCurrentSlide();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
  };

  get sectionId() {
    const section = this.closest('.shopify-section');
    return section?.id.replace(/^shopify-section-/, '');
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.slideshow-control')) {
      this.#focusSuspended = true;
      this.suspend();
      return;
    }

    this.pause();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (!this.#focusSuspended) return;

    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && this.contains(nextTarget)) return;

    this.#focusSuspended = false;
    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
